import fs from 'fs'
import yaml from 'js-yaml'
import { camelCase, snakeCase, pascalCase } from 'change-case'

// TODO: See if we can safely switch to the library implementation

/**
 * Converts a snake_case or SCREAMING_SNAKE_CASE string
 * to either camelCase or PascalCase.
 *
 * @param s - The string to convert.
 * @param pascalCase - If true, returns PascalCase; otherwise camelCase.
 * @returns The converted string.
 */
export function convertSnakeCase(s: string, pascalCase: boolean = false): string {
	return s
		.toLowerCase()
		.split('_')
		.map((word, index) => {
			if (index === 0 && !pascalCase) return word
			return word.charAt(0).toUpperCase() + word.slice(1)
		})
		.join('')
}

/**
 * Reads and parses a YAML configuration file, converting keys to camelCase
 * using `change-case`, and adding extra environment variables.
 *
 * @param filePath - The path to the YAML file to read.
 * @param envVarMappings - A record mapping config keys to environment variable names.
 * @returns The parsed and modified configuration object.
 * @throws Will throw an error if the YAML file cannot be read or parsed.
 */
function readYamlConfig<T = any>(filePath: string, envVarMappings: Record<string, string> = {}): T {
	try {
		console.log(`Reading YAML file at ${filePath}`)
		const fileContents = fs.readFileSync(filePath, 'utf8')
		const parsed = yaml.load(fileContents) as any

		// Recursively camelCase all keys
		const camelCased = toCamelCaseDeep(parsed) as T
		console.log('Parsed YAML (camelCased):', camelCased)

		// Inject env vars
		const withEnvs = addExtraEnvVars(camelCased, envVarMappings)
		console.log('Config with environment variables:', withEnvs)

		return withEnvs
	} catch (err) {
		console.error(`Failed to read or parse YAML file at ${filePath}:`, err)
		throw err
	}
}

/**
 * Deeply converts all object keys to camelCase using `change-case`'s camelCase().
 */
function toCamelCaseDeep(obj: any): any {
	if (obj === null || typeof obj !== 'object') {
		return obj
	}
	if (Array.isArray(obj)) {
		return obj.map(toCamelCaseDeep)
	}
	return Object.entries(obj).reduce((acc, [key, val]) => {
		// use change-case's camelCase to transform keys
		const newKey = camelCase(key)
		acc[newKey] = toCamelCaseDeep(val)
		return acc
	}, {} as any)
}

/**
 * Adds extra environment variables to the configuration object.
 *
 * @param config - The configuration object to modify.
 * @param envVarMappings - A record mapping config keys to environment variable names.
 * @returns The modified configuration object with environment variables added.
 */
function addExtraEnvVars(config: any, envVarMappings: Record<string, string>): any {
	for (const [configKey, envVarName] of Object.entries(envVarMappings)) {
		// assume envVarName is already in SCREAMING_SNAKE_CASE or similar
		config[configKey] = process.env[envVarName]
	}
	return config
}

export const keyify = (obj: any, prefix = ''): any =>
	Object.keys(obj).reduce((res, el) => {
		if (Array.isArray(obj[el])) {
			return res
		} else if (typeof obj[el] === 'object' && obj[el] !== null) {
			return [...res, ...keyify(obj[el], prefix + el + '.')]
		}
		return [...res, prefix + el]
	}, [])

export { readYamlConfig }
